import { useMemo, useState } from "react";
import { CalendarPlus, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useVault } from "@/context/VaultContext";
import { createNote } from "@/lib/store";
import { COLORS, ENTRY_TYPES, FOLDER_COLORS, apptTime, noteColor, noteDate } from "@/lib/calendar";
import { stamp, ymd } from "@/lib/util";
import { ColorSwatches } from "./ColorSwatches";

const nice = (d) => new Date(`${d}T00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

function EntryRow({ n, onOpen, showDate }) {
  const { folderName } = useVault();
  return (
    <button type="button" onClick={onOpen} data-testid={`day-entry-${n.id}`}
      className="w-full max-w-full min-w-0 text-left rounded-md border p-2.5 flex items-start gap-2.5 hover:bg-muted transition-colors">
      <span className="mt-1 h-3 w-3 rounded-full shrink-0" style={{ background: noteColor(n) }} />
      <span className="min-w-0 flex-1 overflow-hidden">
        <span className="block text-sm font-medium truncate">{n.title || `${folderName(n.folderId)} entry`}</span>
        <span className="block text-[11px] text-muted-foreground truncate">
          {showDate ? `${noteDate(n)} · ` : ""}{apptTime(n) ? `${apptTime(n)} · ` : ""}{folderName(n.folderId)}{n.location ? ` · ${n.location}` : ""}{n.scheduledFor ? "" : ` · logged ${stamp(new Date(n.createdAt)).slice(11, 16)}`}
        </span>
      </span>
      {n.entryType && n.entryType !== "Note" && <Badge variant={n.entryType === "Deadline" ? "destructive" : "secondary"} className="text-[10px] shrink-0">{n.entryType}</Badge>}
    </button>
  );
}

function NewEntryForm({ date }) {
  const { folders, folderId, refresh } = useVault();
  const [title, setTitle] = useState("");
  const [type, setType] = useState("Note");
  const [fid, setFid] = useState(folderId);
  const [color, setColor] = useState("");
  const [time, setTime] = useState("");
  const appt = fid === "appointments";
  const future = date > ymd(new Date());
  const create = async () => {
    await createNote(fid, { title: title.trim(), scheduledFor: date, entryType: appt ? "Appointment" : type, color, ...(appt && time ? { apptAt: `${date}T${time}` } : {}) });
    await refresh();
    setTitle(""); setColor(""); setTime("");
    const t = appt ? "Appointment" : type;
    toast.success(future ? `${t} scheduled for ${date}` : `${t} added to ${date}`);
  };
  return (
    <div className="rounded-lg border p-3 space-y-3 min-w-0 max-w-full overflow-hidden" data-testid="new-calendar-entry-form">
      <p className="flex items-center gap-2 text-sm font-medium"><CalendarPlus className="h-4 w-4" />{future ? "Schedule ahead" : "Add entry to this date"}</p>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={appt ? "Appointment Title" : type === "Deadline" ? "e.g. Court filing due" : "Title"} data-testid="calendar-entry-title-input" onKeyDown={(e) => e.key === "Enter" && create()} />
      <div className="grid grid-cols-2 gap-2">
        <Select value={appt ? "Appointment" : type} onValueChange={setType} disabled={appt}>
          <SelectTrigger data-testid="calendar-entry-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{ENTRY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fid} onValueChange={setFid}>
          <SelectTrigger data-testid="calendar-entry-folder-select"><SelectValue /></SelectTrigger>
          <SelectContent>{folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {appt && <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="calendar-entry-time-input" aria-label="Appointment time" />}
      <div className="space-y-1.5"><Label className="text-xs">Color tag</Label>
        <ColorSwatches value={color} onChange={setColor} autoColor={COLORS[FOLDER_COLORS[fid] || "slate"]} testPrefix="calendar-entry-color" />
      </div>
      <Button className="w-full" onClick={create} data-testid="calendar-entry-create-button">{future ? "Schedule" : "Add"} {appt ? "Appointment" : type}</Button>
    </div>
  );
}

export function DayPanel({ date, entries }) {
  const { notes, setNoteId } = useVault();
  const today = ymd(new Date());
  const upcoming = useMemo(() => notes.filter((n) => n.scheduledFor && n.scheduledFor >= today)
    .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor)).slice(0, 8), [notes, today]);
  return (
    <aside className="space-y-4 min-w-0 max-w-full" data-testid="day-panel">
      <div className="min-w-0">
        <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{date < today ? "Past" : date === today ? "Today" : "Upcoming"}</p>
        <h2 className="font-heading text-lg font-semibold break-words min-w-0" data-testid="day-panel-title">{nice(date)}</h2>
      </div>
      <NewEntryForm key={date} date={date} />
      <div className="space-y-2 min-w-0" data-testid="day-entries">
        <p className="text-sm font-medium" data-testid="day-entries-label">On this date</p>
        {entries.map((n) => <EntryRow key={n.id} n={n} onOpen={() => setNoteId(n.id)} />)}
        {!entries.length && <p className="text-sm text-muted-foreground">Nothing on this date yet.</p>}
      </div>
      <div className="space-y-2 min-w-0" data-testid="upcoming-list">
        <p className="flex items-center gap-2 text-sm font-medium"><Clock className="h-4 w-4" />Upcoming</p>
        {upcoming.map((n) => <EntryRow key={n.id} n={n} showDate onOpen={() => setNoteId(n.id)} />)}
        {!upcoming.length && <p className="text-xs text-muted-foreground">No scheduled items ahead.</p>}
      </div>
    </aside>
  );
}
